/**
 * MaskBackend 구현 (설계문서 §5.3).
 *
 * `localBackend`(브라우저 내 추론)는 무겁기 때문에 별도 패키지 `@nukki/local`로
 * 나간다. 원격만 쓰는 소비처가 transformers.js를 끌고 갈 이유가 없다.
 */
import type { MaskBackend, SurfaceImage } from './types.js';

export interface RemoteBackendOptions {
  /** 서버 루트. 같은 오리진이면 `''`. */
  baseUrl?: string;
  /** `/v1/mask` 앞에 붙는 경로. we-together처럼 라우터를 마운트한 경우에 쓴다. */
  path?: string;
  headers?: Record<string, string>;
  timeoutMs?: number;
}

/**
 * 원격 누끼 서버.
 *
 * 서버는 **RGBA PNG(RGB=흰색, 알파=마스크)**를 반환해야 한다.
 * 흑백(L)이면 클라이언트가 휘도->알파 변환에 `getImageData`를 써야 하고
 * 그게 iOS 캔버스 예산을 먹는다 (설계문서 §5.2).
 */
export function remoteBackend(opts: RemoteBackendOptions = {}): MaskBackend {
  const { baseUrl = '', path = '/v1', headers = {}, timeoutMs = 60_000 } = opts;

  return {
    kind: 'remote',
    async getMask(image, { signal } = {}) {
      const fd = new FormData();
      fd.append('file', image, 'image');

      // 호출자 signal 과 자체 타임아웃을 합친다
      const ac = new AbortController();
      const onAbort = () => ac.abort();
      signal?.addEventListener('abort', onAbort, { once: true });
      const timer = setTimeout(() => ac.abort(), timeoutMs);

      try {
        const res = await fetch(`${baseUrl}${path}/mask`, {
          method: 'POST',
          body: fd,
          headers,
          signal: ac.signal,
        });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const body = (await res.json()) as { detail?: string };
            if (body.detail) msg = body.detail;
          } catch { /* 본문이 JSON이 아님 */ }
          throw new Error(msg);
        }
        return (await createImageBitmap(await res.blob())) as SurfaceImage;
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
      }
    },
  };
}
